import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = { depositDate: "", amount: "", bankAccountName: "", depositedById: "", remarks: "" };

const CashDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadDeposits = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/cash-deposits");
      setDeposits(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load cash deposits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeposits();
    api.get("/api/v1/sales-persons").then((data) => setSalesPeople(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/v1/cash-deposits", {
        depositDate: formData.depositDate || undefined,
        amount: Number(formData.amount),
        bankAccountName: formData.bankAccountName,
        depositedById: formData.depositedById ? Number(formData.depositedById) : null,
        remarks: formData.remarks || undefined,
      });
      toast.success("Cash deposit recorded");
      setShowModal(false);
      loadDeposits();
    } catch (error) {
      toast.error(error.message || "Failed to record cash deposit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Physical cash deposited into a bank account — this is the "credit" side of the cash-in-hand ledger, reducing
        cash on hand.
      </div>

      <div className="filters-bar">
        <button className="primary" onClick={openCreate} style={{ marginLeft: "auto" }}>
          + New Deposit
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Deposit #</th>
              <th>Date</th>
              <th>Bank Account</th>
              <th>Deposited By</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {deposits.length === 0 ? (
              <tr>
                <td colSpan={5}>No cash deposits found.</td>
              </tr>
            ) : (
              deposits.map((d) => (
                <tr key={d.id}>
                  <td>{d.depositNumber}</td>
                  <td>{d.depositDate}</td>
                  <td>{d.bankAccountName}</td>
                  <td>{d.depositedByName || "-"}</td>
                  <td>{money(d.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Record Cash Deposit</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Bank Account</label>
                  <input name="bankAccountName" value={formData.bankAccountName} onChange={handleChange} placeholder="HDFC Bank" required />
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input type="number" min="0" step="0.01" name="amount" value={formData.amount} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Deposited By (optional)</label>
                  <select name="depositedById" value={formData.depositedById} onChange={handleChange}>
                    <option value="">Unassigned</option>
                    {salesPeople.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.employeeCode}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="depositDate" value={formData.depositDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input name="remarks" value={formData.remarks} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CashDeposits;
